// Curated "Recommended next read" links.
//
// Each key is a page slug (markdown post slug, or the slug of a standalone
// route like the retire-at-* spokes). The value is the single best next step
// for a reader who just finished that page, plus a one-line reason. Targets
// can be posts or tools — anything with an internal path.
//
// Curated rather than category-derived so the flow follows the actual reader
// journey (framework → number → tool → tracking) instead of publish dates.

export interface NextRead {
  /** Internal path, e.g. "/blog/what-is-coast-fire" or "/tools/…" */
  href: string;
  /** Card headline */
  title: string;
  /** Why this is the logical next step */
  reason: string;
  /** Small eyebrow label */
  kind: "Guide" | "Calculator" | "Comparison";
}

export const NEXT_READS: Record<string, NextRead> = {
  // ---- FIRE cluster ----
  "what-is-coast-fire": {
    href: "/blog/coast-fire-vs-barista-fire",
    title: "Coast FIRE vs Barista FIRE vs Lean FIRE",
    reason:
      "You know the Coast FIRE maths — now see how the other variants change the target number, and which one actually fits your life.",
    kind: "Guide",
  },
  "coast-fire-vs-barista-fire": {
    href: "/tools/coast-fire-calculator",
    title: "Coast FIRE Calculator",
    reason:
      "Put your own age, balance and savings rate in and get the coast number for the variant you picked.",
    kind: "Calculator",
  },
  "how-to-build-passive-income": {
    href: "/blog/best-dividend-stocks-2026",
    title: "Best Dividend Stocks of 2026",
    reason:
      "Passive income needs actual holdings behind it — this is how to judge which dividend payers are worth owning.",
    kind: "Guide",
  },
  "best-dividend-stocks-2026": {
    href: "/tools/dividend-calculator",
    title: "Dividend Income Calculator",
    reason:
      "Project what a given portfolio and yield actually pay you per month, before and after withholding tax.",
    kind: "Calculator",
  },
  "best-dividend-trackers-2026": {
    href: "/blog/how-to-build-passive-income",
    title: "How to Build Passive Income in 2026",
    reason:
      "Once you can track income, the next question is how much capital each income level really needs.",
    kind: "Guide",
  },
  "portfolio-tracker-vs-spreadsheet": {
    href: "/blog/best-dividend-trackers-2026",
    title: "Best Free Dividend Trackers of 2026",
    reason:
      "If the spreadsheet has to go, this compares the tools that replace it — and what each one misses.",
    kind: "Comparison",
  },

  // ---- Retirement spokes ----
  "how-much-to-retire-at-50": {
    href: "/blog/coast-fire-vs-barista-fire",
    title: "Coast FIRE vs Barista FIRE vs Lean FIRE",
    reason:
      "Retiring at 50 usually means picking a FIRE variant rather than full replacement — here's what each one costs.",
    kind: "Guide",
  },
  "how-much-to-retire-at-55": {
    href: "/blog/what-is-coast-fire",
    title: "What Is Coast FIRE?",
    reason:
      "The milestone you'll hit years before the full number: the balance where compounding alone gets you there.",
    kind: "Guide",
  },
  "how-much-to-retire-at-60": {
    href: "/blog/how-to-build-passive-income",
    title: "How to Build Passive Income in 2026",
    reason:
      "The bridge years before pensions start are exactly where investment income does the heavy lifting.",
    kind: "Guide",
  },
  "how-much-to-retire-at-67": {
    href: "/tools/retirement-calculator",
    title: "Retirement Calculator",
    reason:
      "Run your own pension gap with your salary, target age and country's withdrawal tax.",
    kind: "Calculator",
  },

  // ---- AI / personal-finance cluster ----
  "ai-for-personal-finance": {
    href: "/blog/will-ai-replace-financial-advisors",
    title: "Will AI Replace Financial Advisors?",
    reason:
      "The trust question the whole hub rests on, answered with what AI can and cannot legally or practically do.",
    kind: "Guide",
  },
  "will-ai-replace-financial-advisors": {
    href: "/blog/best-ai-investing-apps",
    title: "Best AI Investing Apps of 2026",
    reason:
      "If AI is an assistant rather than an advisor, this is how the current crop of tools actually compares.",
    kind: "Comparison",
  },
  "best-ai-investing-apps": {
    href: "/blog/ai-for-financial-analysis",
    title: "AI for Financial Analysis",
    reason:
      "What the analysis layer inside these apps is really doing to your numbers — and where it goes wrong.",
    kind: "Guide",
  },
  "ai-for-financial-analysis": {
    href: "/blog/how-ai-portfolio-analysis-works",
    title: "How AI Portfolio Analysis Actually Works",
    reason:
      "A look under the hood at the portfolio-specific version, including the limits worth knowing.",
    kind: "Guide",
  },
  "how-ai-portfolio-analysis-works": {
    href: "/blog/apple-stock-iphone-launch-history",
    title: "Apple Stock After Every iPhone Launch: 20 Years of Data",
    reason:
      "A worked example of reading one company's history properly — and of the concentration risk a single winner creates.",
    kind: "Guide",
  },
  "apple-stock-iphone-launch-history": {
    href: "/halal-investing",
    title: "The Halal Investing Guide",
    reason:
      "Single-stock screening done properly: business-activity and balance-sheet screens, plus the purification step.",
    kind: "Guide",
  },

  // ---- Brokers ----
  "best-stock-brokers-europe-uk-2026": {
    href: "/blog/trading-212-vs-trade-republic-vs-interactive-brokers",
    title: "Trading 212 vs Trade Republic vs Interactive Brokers",
    reason:
      "Narrow the shortlist down to a head-to-head on fees, FX, ISA access and API support.",
    kind: "Comparison",
  },
  "trading-212-vs-trade-republic-vs-interactive-brokers": {
    href: "/blog/portfolio-tracker-vs-spreadsheet",
    title: "Portfolio Tracker vs Spreadsheet",
    reason:
      "Once holdings sit at more than one broker, the tracking question comes next.",
    kind: "Comparison",
  },
};

export function getNextRead(slug: string): NextRead | undefined {
  return NEXT_READS[slug];
}
